import React, { useState } from "react";

import {
  Badge,
  Box,
  Button,
  Checkbox,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Tab,
  Table,
  TableContainer,
  TabList,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure
} from "@chakra-ui/react";

import { FiSearch, FiUserPlus } from "react-icons/fi";

import { EmailForwardingSender } from "@/jenesys-types/jenesys-entities/config";

import { AddSender } from "@/components/features/client/AddSender";
import { ConfirmSender } from "@/components/features/client/ConfirmSender";
import { UploadCSV } from "@/components/features/client/UploadCsv";
import { useEmailForwardingSenders } from "@/hooks/features/useEmailForwardingSenders";
import { useCompany } from "@/hooks/util/useCompany";
import { formatDate } from "@/util/helpers";

export interface EmailForwardingSendersProps {}

export const EmailForwardingSenders: React.FC<
  EmailForwardingSendersProps
> = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string>("ALLOWED");
  const blockDisclosure = useDisclosure();
  const revokeDisclosure = useDisclosure();
  const allowDisclosure = useDisclosure();
  const addSenderDisclosure = useDisclosure();
  const uploadCSVDisclosure = useDisclosure();
  const [selectedSender, setSelectedSender] =
    useState<EmailForwardingSender | null>(null);
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    position: ""
  });
  const { activeClientId } = useCompany();

  const {
    senders,
    isAddingSender,
    isUpdatingSender,
    isUploadingCSV,
    handleAddSender,
    handleUpdateSender,
    handleUploadCSV
  } = useEmailForwardingSenders({ clientId: activeClientId ?? "" });

  const getStatusCount = (status: string) =>
    senders.filter((sender: EmailForwardingSender) => sender?.status === status)
      .length;

  const filteredSenders = senders.filter((sender: EmailForwardingSender) => {
    if (!sender) return false;
    const matchesSearch =
      sender?.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sender?.email.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesSearch && sender.status === selectedStatus;
  });

  return (
    <Box mt={8} rounded={16} pt={7} pb={16} bg="white">
      <Box px={6}>
        <Heading as="h1" fontSize={16} fontWeight={600} color="primary" mb={2}>
          Email Forwarding Senders
        </Heading>
        <Text color="secondary.700" fontSize={13} mb={3}>
          A safe senders list for invoices is a security measure that authorises
          specific email addresses or domains to send invoices to your company.
        </Text>

        <Tabs
          mb={6}
          onChange={(index) => {
            const statuses = ["ALLOWED", "PENDING", "REJECTED", "BLOCKED"];
            setSelectedStatus(statuses[index]);
          }}
          colorScheme="primary"
        >
          <TabList>
            <Tab fontWeight={500} fontSize={13}>
              Safe senders
            </Tab>
            <Tab fontWeight={500} fontSize={13}>
              Pending
              <Badge ml={2} colorScheme="gray" borderRadius="full">
                {getStatusCount("PENDING")}
              </Badge>
            </Tab>
            <Tab fontWeight={500} fontSize={13}>
              Revoked
            </Tab>
            <Tab fontWeight={500} fontSize={13}>
              Blocked Senders
            </Tab>
          </TabList>
        </Tabs>

        <HStack spacing={4} mb={6}>
          <InputGroup flex={1}>
            <InputLeftElement pointerEvents="none">
              <FiSearch color="gray.500" />
            </InputLeftElement>
            <Input
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or email"
              bg="gray.50"
              borderRadius="md"
              fontSize="16px"
              color="black"
              backgroundColor="white"
            />
          </InputGroup>
          {selectedStatus !== "PENDING" && (
            <>
              {selectedStatus === "ALLOWED" && (
                <Button
                  onClick={uploadCSVDisclosure.onOpen}
                  colorScheme="primary"
                  variant="outline"
                  leftIcon={<FiUserPlus />}
                  isLoading={isUploadingCSV}
                  size="lg"
                >
                  Add CSV
                </Button>
              )}
              <Button
                onClick={addSenderDisclosure.onOpen}
                colorScheme="primary"
                leftIcon={<FiUserPlus />}
                size="lg"
              >
                Add user
              </Button>
            </>
          )}
        </HStack>

        <TableContainer>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th
                  pl={0}
                  color="black"
                  textTransform="capitalize"
                  fontWeight={600}
                >
                  Full Name
                </Th>
                <Th color="black" textTransform="capitalize" fontWeight={600}>
                  Email address
                </Th>
                <Th color="black" textTransform="capitalize" fontWeight={600}>
                  Position
                </Th>
                <Th color="black" textTransform="capitalize" fontWeight={600}>
                  Date and Time
                </Th>
                <Th color="black" textTransform="capitalize" fontWeight={600}>
                  Email
                </Th>
                <Th color="black" textTransform="capitalize" fontWeight={600}>
                  Revoked
                </Th>
                <Th color="black" textTransform="capitalize" fontWeight={600}>
                  Blocked
                </Th>
                {(selectedStatus === "REJECTED" ||
                  selectedStatus === "BLOCKED") && (
                  <Th color="black" textTransform="capitalize" fontWeight={600}>
                    Reason
                  </Th>
                )}
              </Tr>
            </Thead>
            <Tbody>
              {filteredSenders.map((sender: EmailForwardingSender) => (
                <Tr key={sender.email}>
                  <Td pl={0}>{sender?.full_name}</Td>
                  <Td>{sender.email}</Td>
                  <Td>{sender.position}</Td>
                  <Td>
                    {sender.created_at ? formatDate(sender.created_at) : ""}
                  </Td>
                  <Td>
                    <Checkbox
                      isChecked={sender.status === "ALLOWED"}
                      onChange={() => {
                        setSelectedSender(sender);
                        allowDisclosure.onOpen();
                      }}
                      isDisabled={isUpdatingSender}
                      size="lg"
                    />
                  </Td>
                  <Td>
                    <Checkbox
                      isChecked={sender.status === "REJECTED"}
                      onChange={() => {
                        setSelectedSender(sender);
                        revokeDisclosure.onOpen();
                      }}
                      isDisabled={isUpdatingSender}
                      size="lg"
                    />
                  </Td>
                  <Td>
                    <Checkbox
                      isChecked={sender.status === "BLOCKED"}
                      onChange={() => {
                        setSelectedSender(sender);
                        blockDisclosure.onOpen();
                      }}
                      isDisabled={isUpdatingSender}
                      size="lg"
                    />
                  </Td>
                  {(selectedStatus === "REJECTED" ||
                    selectedStatus === "BLOCKED") && (
                    <Td>{sender.reason || "-"}</Td>
                  )}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      <AddSender
        isOpen={addSenderDisclosure.isOpen}
        onClose={addSenderDisclosure.onClose}
        senderType={selectedStatus as "ALLOWED" | "REJECTED" | "BLOCKED"}
        isLoading={isAddingSender}
        onAddSender={(data) => {
          setFormData(data);
          if (data.status === "ALLOWED") {
            allowDisclosure.onOpen();
          } else if (data.status === "REJECTED") {
            revokeDisclosure.onOpen();
          } else if (data.status === "BLOCKED") {
            blockDisclosure.onOpen();
          }
        }}
      />

      <UploadCSV
        isOpen={uploadCSVDisclosure.isOpen}
        onClose={uploadCSVDisclosure.onClose}
        isUploading={isUploadingCSV}
        onUpload={(file: File) => {
          handleUploadCSV(file);
          uploadCSVDisclosure.onClose();
        }}
      />

      <ConfirmSender
        isOpen={blockDisclosure.isOpen}
        onClose={() => {
          blockDisclosure.onClose();
        }}
        title="Blocklist Confirmation"
        description="By clicking 'Confirm', you will permanently add [email] to the blocklist. This action is irreversible without administrator access. Please confirm you understand the implications."
        senderEmail={selectedSender?.email || formData.email}
        confirmText="Confirm"
        loadingText="Blocking..."
        isLoading={isUpdatingSender}
        defaultReason="Phishing/Fraudulent Activity"
        onConfirm={(reason) => {
          if (selectedSender) {
            handleUpdateSender(selectedSender.id, {
              status: "BLOCKED",
              reason
            });
          } else {
            handleAddSender({
              ...formData,
              status: "BLOCKED",
              reason
            });
          }
          setFormData({
            full_name: "",
            email: "",
            position: ""
          });
        }}
        senderType="BLOCKED"
      />

      <ConfirmSender
        isOpen={revokeDisclosure.isOpen}
        onClose={() => {
          revokeDisclosure.onClose();
        }}
        title="Revocation Confirmation"
        description="By clicking 'Confirm', you will add [email] to the revocation list. Please add reason and confirm you understand the implications."
        senderEmail={selectedSender?.email || formData.email}
        confirmText="Confirm"
        loadingText="Revoking..."
        isLoading={isUpdatingSender}
        defaultReason="No Longer Authorized"
        onConfirm={(reason) => {
          if (selectedSender) {
            handleUpdateSender(selectedSender.id, {
              status: "REJECTED",
              reason
            });
          } else {
            handleAddSender({
              ...formData,
              status: "REJECTED",
              reason
            });
          }
          setFormData({
            full_name: "",
            email: "",
            position: ""
          });
        }}
        senderType="REJECTED"
      />

      <ConfirmSender
        isOpen={allowDisclosure.isOpen}
        onClose={allowDisclosure.onClose}
        title="Permission"
        description="You are about to give permission to [email] to upload invoices via Email. Please ensure they resend the invoice that they have initially sent."
        senderEmail={selectedSender?.email || formData.email}
        confirmText="Confirm"
        loadingText="Allowing..."
        isLoading={isUpdatingSender}
        onConfirm={() => {
          if (selectedSender) {
            handleUpdateSender(selectedSender.id, { status: "ALLOWED" });
          } else {
            handleAddSender({
              ...formData,
              status: "ALLOWED"
            });
          }
          setFormData({
            full_name: "",
            email: "",
            position: ""
          });
        }}
        senderType="ALLOWED"
      />
    </Box>
  );
};
